import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import * as Sentry from "@sentry/react";
import ISessionData from "../types/ISessionData";
import { authUrl, post } from "../service/axios";
import { useTranslation } from "react-i18next";

export const LS_SESSION_KEY = "session";

const lsSession = localStorage.getItem(LS_SESSION_KEY);

const defaultSessionData: ISessionData | null = lsSession
  ? JSON.parse(lsSession)
  : null;

export const AuthContext = createContext<ISessionData | null>(
  defaultSessionData
);
export const SessionStartContext = createContext<
  (sessionData: ISessionData) => void
>(() => console.log("Session Context starts"));
export const SessionEndContext = createContext<() => void>(() =>
  console.log("Session Context ends")
);

export function useAuth() {
  return useContext(AuthContext);
}
export function useSessionStart() {
  return useContext(SessionStartContext);
}
export function useSessionEnd() {
  return useContext(SessionEndContext);
}

type SeverityLevel = "fatal" | "error" | "warning" | "log" | "info" | "debug";

function getConsoleByLevel(level: SeverityLevel) {
  switch (level) {
    case "warning":
      return console.warn;
    case "info":
    case "log":
      return console.info;
    case "debug":
      return console.debug;
    default:
      return console.error;
  }
}

export function handleError(
  error: any,
  fallback: string,
  level: SeverityLevel = "error"
) {
  const status = error?.response?.status;
  const data = error?.response?.data;
  const url = error?.config?.url;
  const method = error?.config?.method;

  const extractMessages = (
    data: any
  ): string | Partial<Record<string, string[]>> => {
    if (!data) return fallback;
    if (typeof data === "string") return data;
    if (Array.isArray(data?.details)) return { details: data.details };
    if (typeof data?.details === "string") return data.details;
    if (typeof data?.message === "string") return data.message;
    if (typeof data === "object" && Object.keys(data).length > 0) return data;
    return fallback;
  };

  const alertMsgs = extractMessages(data);

  const plainText =
    typeof alertMsgs === "string"
      ? alertMsgs
      : Array.isArray(alertMsgs)
        ? alertMsgs.join(", ")
        : typeof alertMsgs === "object"
          ? Object.values(alertMsgs).flat().join(", ")
          : fallback;

  const readableText = `[${level.toUpperCase()}] ${fallback}${status ? ` — Status: ${status}` : ""
    }${url ? ` — URL: ${url}` : ""}${plainText ? ` — Details: ${plainText}` : ""}`;

  Sentry.captureMessage(readableText, {
    level,
    tags: { feature: "auth" },
    extra: { fallbackMessage: fallback, status, url, method, responseData: data },
  });

  const log = getConsoleByLevel(level);
  log("[Auth Error]", readableText);
}


export default function AuthProvider({ children }: { children: ReactNode }) {
  const [sessionData, setSessionData] = useState<ISessionData | null>(
    defaultSessionData
  );
  const { t } = useTranslation();

  function sessionStart(sessionData: ISessionData) {
    setSessionData(sessionData);
  }

  function sessionEnd() {
    setSessionData(null);
  }

  async function verifyToken(token: string) {
    try {
      console.log("Verifying token...");
      await post(`${authUrl}/verify/`, { token });
    } catch (error: any) {
      handleError(error, t("errors.verifyTokenFailed"), "error");
      sessionEnd();
    } finally {
      console.log("Token verification finished");
    }
  }

  useEffect(() => {
    if (sessionData) {
      if (!sessionData.otp_required) {
        verifyToken(sessionData.access as string);
      }
      localStorage.setItem(LS_SESSION_KEY, JSON.stringify(sessionData));
      console.log("Session data stored");
    } else {
      console.log("Session cleared");
      localStorage.removeItem(LS_SESSION_KEY);
    }
  }, [sessionData]);

  return (
    <SessionStartContext.Provider value={sessionStart}>
      <SessionEndContext.Provider value={sessionEnd}>
        <AuthContext.Provider value={sessionData}>
          {children}
        </AuthContext.Provider>
      </SessionEndContext.Provider>
    </SessionStartContext.Provider>
  );
}
